import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Collapse,
  CircularProgress,
  Grid,
  Button,
  useTheme,
  alpha,
  styled,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import JsonViewer from 'shared/JsonViewer';
import { format } from 'date-fns';
import { useActivityLogsQuery } from 'ActivityLogModule/query/hooks/useActivityLogsQuery';
import {
  ActivityActionType,
  ActivityActionTypeLabels,
  ActivityLogItem,
  ActivityLogSortBy,
  SortOrder,
} from 'ActivityLogModule/types/activityLog';
import * as constants from '@constants';

const StyledTableContainer = styled(Paper)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.95)',
  borderRadius: '16px',
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor:
    theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(203, 213, 225, 0.5)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
      : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  overflow: 'hidden',
}));

const ExpandedContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(248, 250, 252, 0.8)',
}));

interface ExpandableRowProps {
  log: ActivityLogItem;
  isExpanded: boolean;
  onToggle: () => void;
}

const ExpandableRow: React.FC<ExpandableRowProps> = ({ log, isExpanded, onToggle }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label={isExpanded ? 'Collapse row details' : 'Expand row details'}
            aria-expanded={isExpanded}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {log.occurredAt ? format(new Date(log.occurredAt), 'yyyy-MM-dd HH:mm:ss') : '-'}
        </TableCell>
        <TableCell>{log.username || '-'}</TableCell>
        <TableCell>{log.email || '-'}</TableCell>
        <TableCell>
          {log.actionType ? (
            <Chip
              label={
                ActivityActionTypeLabels[log.actionType as ActivityActionType] || log.actionType
              }
              size="small"
              color="primary"
              sx={{ fontWeight: 500 }}
            />
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell>{log.summary || '-'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <ExpandedContent>
              <Grid container spacing={3}>
                <Grid size={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                      Access Level:
                    </Typography>
                    {log.accessLevel ? (
                      <Chip
                        label={log.accessLevel}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 500 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        -
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                    Details:
                  </Typography>
                  {log.details ? (
                    <Box
                      sx={{
                        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '8px',
                        padding: 1.5,
                      }}
                    >
                      <JsonViewer
                        src={log.details}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        enableClipboard={true}
                        collapsed={1}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      -
                    </Typography>
                  )}
                </Grid>

                <Grid size={12}>
                  <Box display="flex" gap={4}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                        Network Log ID:
                      </Typography>
                      <Typography variant="body2">{log.networkLogId || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                        Action Log ID:
                      </Typography>
                      <Typography variant="body2">{log.actionLogId || '-'}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </ExpandedContent>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

interface TeamActivityLogTabProps {
  teamId: number;
  tenant?: string;
  realmId?: string;
}

export const TeamActivityLogTab: React.FC<TeamActivityLogTabProps> = ({
  teamId,
  tenant = '',
  realmId = '',
}) => {
  // Pagination and sorting state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortBy, setSortBy] = useState<ActivityLogSortBy>(ActivityLogSortBy.occurredAt);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);

  // Expandable rows state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Build query params - pre-filtered for this team
  const queryParams = useMemo(
    () => ({
      tenant: tenant || undefined,
      realmId: realmId || undefined,
      teamId: String(teamId),
      page: page + 1, // Backend uses 1-based indexing
      limit: rowsPerPage,
      sortBy,
      sortOrder,
    }),
    [tenant, realmId, teamId, page, rowsPerPage, sortBy, sortOrder],
  );

  // Fetch activity logs
  const { activityLogs, isLoading } = useActivityLogsQuery(queryParams);

  // Handlers
  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
    setExpandedIds(new Set()); // Clear expanded rows on page change
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedIds(new Set());
  }, []);

  const handleSort = useCallback(
    (column: ActivityLogSortBy) => {
      if (sortBy === column) {
        // Toggle sort order if clicking the same column
        setSortOrder((prev) => (prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC));
      } else {
        // Set new column and default to DESC
        setSortBy(column);
        setSortOrder(SortOrder.DESC);
      }
      setPage(0); // Reset to first page when sorting changes
    },
    [sortBy],
  );

  const handleViewFullPage = () => {
    const params = new URLSearchParams({
      tenant,
      realmId,
      teamId: String(teamId),
    });
    window.open(
      `${constants.ACTIVITY_LOG_PATH}?${params.toString()}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <Box>
      {/* Header with "View in Activity Log Page" link */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Activity Log</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={handleViewFullPage}
        >
          View in Activity Log Page
        </Button>
      </Box>

      {/* Table Section */}
      <StyledTableContainer>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === ActivityLogSortBy.occurredAt}
                      direction={
                        sortBy === ActivityLogSortBy.occurredAt
                          ? (sortOrder.toLowerCase() as 'asc' | 'desc')
                          : 'desc'
                      }
                      onClick={() => handleSort(ActivityLogSortBy.occurredAt)}
                    >
                      Timestamp
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === ActivityLogSortBy.username}
                      direction={
                        sortBy === ActivityLogSortBy.username
                          ? (sortOrder.toLowerCase() as 'asc' | 'desc')
                          : 'desc'
                      }
                      onClick={() => handleSort(ActivityLogSortBy.username)}
                    >
                      Actor
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === ActivityLogSortBy.actionType}
                      direction={
                        sortBy === ActivityLogSortBy.actionType
                          ? (sortOrder.toLowerCase() as 'asc' | 'desc')
                          : 'desc'
                      }
                      onClick={() => handleSort(ActivityLogSortBy.actionType)}
                    >
                      Action Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Summary</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activityLogs?.data && activityLogs.data.length > 0 ? (
                  activityLogs.data.map((log) => (
                    <ExpandableRow
                      key={log.id}
                      log={log}
                      isExpanded={expandedIds.has(log.id)}
                      onToggle={() => handleToggleExpand(log.id)}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary" py={4}>
                        No activity logs found for this team
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={activityLogs?.pagination?.total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </StyledTableContainer>
    </Box>
  );
};
